import { useEffect, useRef } from 'react';

/**
 * FIX #16 — Completely rewritten useFadeIn hook.
 *
 * Root causes of mobile failures in the old version:
 * 1. stagger mode observed every .fade-in child individually, but when ANY
 *    one fired it ran setTimeout for ALL children — meaning children that
 *    were already off-screen got .visible prematurely on small viewports,
 *    or missed it entirely if the last batched timeout ran after the element
 *    had already scrolled out of the rootMargin window.
 * 2. threshold:0.07 + rootMargin:-36px on a 375px viewport means an element
 *    must be at least 36+0.07*elementHeight inside the viewport. For a 56px
 *    card that's ~40px in — fine on desktop but on mobile the short viewport
 *    means cards near the bottom never quite satisfy this before the user
 *    scrolls past them.
 *
 * Fix strategy:
 * - Each .fade-in element gets its OWN observer entry.
 * - When it intersects, it gets .visible immediately (no batching delay bug).
 * - threshold is 0 so any pixel of the element in view triggers it.
 * - rootMargin is reduced to avoid over-restricting on mobile.
 * - Stagger is handled by CSS transition-delay instead of JS setTimeout.
 * - A fallback adds .visible to ALL elements after 2s so JS failures
 *   never leave content invisible.
 */
export default function useFadeIn(stagger = false) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Find all fade-in targets within this section
    const targets = stagger
      ? Array.from(el.querySelectorAll('.fade-in'))
      : [el.classList.contains('fade-in') ? el : null].filter(Boolean);

    if (targets.length === 0) {
      // No targets — make sure the section itself is visible
      el.style.opacity = '1';
      el.style.transform = 'none';
      return;
    }

    // Apply staggered CSS transition-delay (pure CSS, no JS timeout race)
    if (stagger) {
      targets.forEach((t, i) => {
        t.style.transitionDelay = `${Math.min(i * 60, 360)}ms`;
      });
    }

    // Safety net: after 2.5s force-show everything (handles hidden iOS tabs,
    // print media, reduced-motion, and slow connections)
    const safetyTimer = setTimeout(() => {
      targets.forEach(t => {
        t.classList.add('visible');
        t.style.transitionDelay = '0ms';
      });
      el.querySelectorAll('.sec-title').forEach(t => t.classList.add('animated'));
    }, 2500);

    // Create a single observer for all targets
    const observer = new IntersectionObserver(
      (entries) => {
        let anyVisible = false;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
            anyVisible = true;
          }
        });
        // Also trigger sec-title underline animation when section comes into view
        if (anyVisible) {
          el.querySelectorAll('.sec-title:not(.animated)').forEach(t => {
            t.classList.add('animated');
          });
        }
      },
      {
        // FIX: threshold:0 = any pixel visible triggers it (most reliable on mobile)
        threshold: 0,
        // FIX: smaller negative rootMargin — don't over-restrict on mobile viewports
        rootMargin: '0px 0px -20px 0px',
      }
    );

    targets.forEach(t => observer.observe(t));

    // FIX: Also watch for DOM changes (like skeletons being replaced by real cards)
    const mutationObserver = new MutationObserver(() => {
      const newTargets = Array.from(el.querySelectorAll('.fade-in:not(.visible)'));
      newTargets.forEach((t, i) => {
        if (stagger) {
          t.style.transitionDelay = `${Math.min(i * 60, 360)}ms`;
        }
        observer.observe(t);
      });
    });

    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      clearTimeout(safetyTimer);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [stagger]);

  return ref;
}
